import React, { useEffect, useRef } from "react";
import app from "nystem";

const ext2type = { m4a: "audio/x-m4a", mp3: "audio/mpeg" };
// Buffering without receiving data for this long while playing reloads the stream at the same position.
const stallTimeout = 15000;
const maxRetries = 5;

const Audio = ({ src, controls, preload, className, playing, onChange, updateInterval, currentTime }) => {
  const ref = useRef();
  const source = useRef();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const playingRef = useRef(playing);
  playingRef.current = playing;

  useEffect(() => {
    if (!ref.current) return;
    if (playing) {
      // After the retries gave up, the element needs a new load before it can play.
      if (ref.current.error || ref.current.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) ref.current.load();
      ref.current.play()?.catch((error) => {
        // A reload or a pause aborts a pending play; only a refused play ends playback.
        if (error.name !== "AbortError") onChangeRef.current?.({ playing: false });
      });
    } else ref.current.pause();
  }, [playing, src]);

  useEffect(() => {
    if (playing && ref.current && currentTime && Math.abs(ref.current.currentTime - currentTime) > 0.1)
      ref.current.currentTime = currentTime;
  }, [currentTime, playing]);

  useEffect(() => {
    const audio = ref.current;
    if (!audio) return;
    const { current: sourceElement } = source;
    const report = (settings) => onChangeRef.current?.(settings);

    // Position to restore while the stream reloads after a network failure.
    let resume;
    let retries = 0;
    let retryTimer;
    let stallTimer;
    let pauseTimer;
    let buffering = false;

    const reload = () => {
      window.removeEventListener("online", reload);
      audio.load();
    };
    const retry = () => {
      clearTimeout(stallTimer);
      if (retryTimer) return;
      if (retries >= maxRetries) {
        resume = undefined;
        report({ playing: false, isPlaying: false, error: { src } });
        return;
      }
      resume ??= audio.currentTime;
      report({ isPlaying: false });
      // Offline, wait for the network instead of spending retries.
      if (!navigator.onLine) return window.addEventListener("online", reload);
      retryTimer = setTimeout(() => {
        retryTimer = undefined;
        reload();
      }, 1000 * 2 ** retries++);
    };

    const error = () => {
      // Decoding and format errors do not improve by reloading.
      if ([3, 4].includes(audio.error?.code)) {
        console.log("error", src, audio.error.code);
        report({ playing: false, isPlaying: false, error: { src } });
      } else if (audio.error?.code !== 1) retry();
    };
    audio.addEventListener("error", error);
    // With <source> children, a failed request fires here instead of on the element.
    sourceElement.addEventListener("error", retry);

    const loadedmetadata = () => {
      if (resume === undefined) return;
      audio.currentTime = resume;
      resume = undefined;
      if (playingRef.current) audio.play()?.catch(() => {});
    };
    audio.addEventListener("loadedmetadata", loadedmetadata);

    const loaded = () => {
      report({ duration: parseInt(audio.duration, 10) });
    };
    audio.addEventListener("loadeddata", loaded);

    const watchStall = () => {
      clearTimeout(stallTimer);
      if (buffering && !audio.paused) stallTimer = setTimeout(retry, stallTimeout);
    };
    // A slow stream that still delivers data is not stalled.
    audio.addEventListener("progress", watchStall);

    const waiting = () => {
      buffering = true;
      report({ isPlaying: false });
      watchStall();
    };
    audio.addEventListener("waiting", waiting);

    const playingEvent = () => {
      buffering = false;
      clearTimeout(stallTimer);
      // Playback recovered on its own, so a scheduled reload would only jump back.
      clearTimeout(retryTimer);
      retryTimer = undefined;
      window.removeEventListener("online", reload);
      resume = undefined;
      retries = 0;
      report({ isPlaying: true });
    };
    audio.addEventListener("playing", playingEvent);

    const play = () => {
      report({ playing: true });
      app.event("media", { audio });
    };
    audio.addEventListener("play", play);

    const paused = () => {
      clearTimeout(stallTimer);
      clearTimeout(pauseTimer);
      pauseTimer = setTimeout(() => {
        if (resume === undefined) report({ playing: false, isPlaying: false });
      }, 100);
    };
    audio.addEventListener("pause", paused);

    const ended = () => {
      report({ currentTime: 0, playing: false, isPlaying: false });
    };
    audio.addEventListener("ended", ended);

    const updateTick = setInterval(() => {
      if (!audio.paused && resume === undefined)
        report({ currentTime: audio.currentTime, duration: audio.duration, isPlaying: audio.readyState >= 3 });
    }, updateInterval || 1000);

    const overlay = (data) => {
      if (data.do === "open") report({ playing: false });
    };
    app.on("overlay", overlay);

    return () => {
      clearInterval(updateTick);
      clearTimeout(retryTimer);
      clearTimeout(stallTimer);
      clearTimeout(pauseTimer);
      window.removeEventListener("online", reload);
      app.off("overlay", overlay);
      audio.removeEventListener("error", error);
      sourceElement.removeEventListener("error", retry);
      audio.removeEventListener("loadedmetadata", loadedmetadata);
      audio.removeEventListener("loadeddata", loaded);
      audio.removeEventListener("waiting", waiting);
      audio.removeEventListener("progress", watchStall);
      audio.removeEventListener("playing", playingEvent);
      audio.removeEventListener("play", play);
      audio.removeEventListener("pause", paused);
      audio.removeEventListener("ended", ended);
    };
  }, [src, updateInterval]);

  if (!src) return src;
  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <audio className={className} ref={ref} controls={controls} preload={preload}>
      <source ref={source} src={src} type={ext2type[src.substring(src.lastIndexOf(".") + 1)]} />
      Your browser does not support the audio element.
    </audio>
  );
};
export default Audio;
